/**
 * @fileoverview The picture that has been chosen, and the day it says it was taken.
 *
 * Camera, file picker and Google Photos all end here: whichever way a picture
 * arrives it is run through preparePhoto, which shrinks it and reads its
 * EXIF, and the date it claims becomes the date offered. The three callers
 * used to each repeat that sequence with slightly different fallbacks.
 *
 * An iPhone photo (HEIC) is made a JPEG on the way through, its date still
 * read from the original. One that cannot be converted is not kept: the
 * server would refuse it, so the choice is cleared and why is said instead.
 *
 * Processing takes a moment (seconds, for an iPhone photo). A result that
 * arrives after the choice was cleared, or after another picture was chosen,
 * is dropped: it used to put an abandoned photo back in a closed dialog,
 * ready to upload the next time it opened.
 *
 * Where: UploadPhotoDialog.
 * API: none.
 * Settings: none.
 * Words (en/es): none of its own; a picture that cannot be converted is
 * refused in preparePhoto's message.
 *
 * @module usePhotoChoice
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { getCurrentDate } from '@/shared/utils/dateUtils';

/** Where the offered date came from, which is worth saying out loud. */
export type DateSource = 'exif' | 'default' | 'user';

/** The chosen picture, its date, and the verbs that change them. */
export interface PhotoChoice {
  /** The processed file, ready to upload, or null when none is chosen. */
  file: File | null;
  /** An object URL for showing it. Revoked when the choice is cleared. */
  previewUrl: string | null;
  /** True while a chosen file is being processed (converted, shrunk, dated). */
  isProcessing: boolean;
  /** Why the last picture chosen could not be used, in the reader's language, or null. */
  error: string | null;
  /** The day offered for the picture, as a local calendar day. */
  dateTaken: Date;
  /** Where dateTaken came from, so the field can say "from the photo". */
  dateSource: DateSource;
  /** Google Photos' id for the chosen picture, when it was picked there; null otherwise (B61). */
  googleItemId: string | null;
  /**
   * Takes a picture from any of the three sources.
   *
   * @param incoming - The raw file as the source handed it over.
   * @param options.trustExif - False for camera captures, which were taken
   *   just now whatever a re-encoded header might claim.
   * @param options.takenAt - A date the source already knows, which beats
   *   anything the file says. Google Photos is the case: the bytes it hands
   *   over are a resize with no EXIF left in them, and the real date arrives
   *   beside the file rather than inside it.
   * @param options.googleItemId - Google Photos' id for the item, sent with the upload.
   */
  accept: (
    incoming: File,
    options?: { trustExif?: boolean; takenAt?: Date | null; googleItemId?: string },
  ) => Promise<void>;
  /** Overrides the date, as the gardener picking one in the calendar does. */
  setDate: (date: Date) => void;
  /** Forgets the chosen picture. */
  clear: () => void;
}

/** Today, floored, which is what an undated picture gets. */
const today = (): Date => getCurrentDate().startOf('day').toJSDate();

/**
 * Holds one chosen picture for a dialog: processed, previewed and dated.
 *
 * @returns The choice and its verbs (PhotoChoice).
 */
export function usePhotoChoice(): PhotoChoice {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** The object URL on screen, kept so it can be revoked when it is replaced. */
  const previewRef = useRef<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dateTaken, setDateTaken] = useState<Date>(today);
  const [googleItemId, setGoogleItemId] = useState<string | null>(null);
  const [dateSource, setDateSource] = useState<DateSource>('default');
  const [error, setError] = useState<string | null>(null);
  /** Counts choices and clears, so a result knows whether it is still wanted. */
  const attempt = useRef(0);

  /**
   * Puts a preview on screen and lets go of the one before it. Each object
   * URL pins its whole file in memory until revoked, and a phone photo is
   * several megabytes.
   *
   * @param url - The new preview, or null for none.
   */
  const show = useCallback((url: string | null) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = url;
    setPreviewUrl(url);
  }, []);

  // And the last one goes when the dialog does.
  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = null;
  }, []);

  /**
   * Takes a picture from any of the three sources: shown at once, then
   * processed and dated (see PhotoChoice.accept for the options).
   */
  const accept = useCallback(async (
    incoming: File,
    { trustExif = true, takenAt = null, googleItemId }: { trustExif?: boolean; takenAt?: Date | null; googleItemId?: string } = {}
  ): Promise<void> => {
    attempt.current += 1;
    const mine = attempt.current;
    setGoogleItemId(googleItemId ?? null);
    setIsProcessing(true);
    setError(null);
    // Shown straight away: processing a phone photo takes a moment, and a
    // blank dialog in the meantime looks like nothing happened.
    setFile(incoming);
    show(URL.createObjectURL(incoming));

    const prepared = await preparePhoto(incoming);
    // Cleared, or another picture chosen, while this one was processed.
    if (mine !== attempt.current) return;
    setIsProcessing(false);
    // A picture that could not be made ready (an iPhone photo that would not
    // convert, a wrong type) is not sent: it is let go and the reason shown.
    if (!prepared.ok) {
      setFile(null);
      show(null);
      setError(prepared.message);
      return;
    }
    setFile(prepared.file);
    show(URL.createObjectURL(prepared.file));
    // A date the source stated outright is better evidence than a header
    // that may not have survived being re-encoded on the way here.
    if (takenAt) {
      setDateTaken(takenAt);
      setDateSource('exif');
      return;
    }
    const exifDate = trustExif ? prepared.exif.dateTaken : undefined;
    setDateTaken(exifDate ? exifDate.toJSDate() : today());
    setDateSource(exifDate ? 'exif' : 'default');
  }, [show]);

  /**
   * The gardener's own date, which outranks whatever the picture said.
   *
   * @param date - The day chosen.
   */
  const setDate = useCallback((date: Date) => {
    setDateTaken(date);
    setDateSource('user');
  }, []);

  /** Back to nothing chosen, dated today. */
  const clear = useCallback(() => {
    attempt.current += 1;
    setFile(null);
    show(null);
    setIsProcessing(false);
    setError(null);
    setDateTaken(today());
    setDateSource('default');
    setGoogleItemId(null);
  }, [show]);

  return { file, previewUrl, isProcessing, error, dateTaken, dateSource, googleItemId, accept, setDate, clear };
}
